import React from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { NavBar, NavActions } from "@/components/NavBar";
import { Footer } from "@/components/Footer";
import {
  ArrowLeft, MapPin, Star, Camera, DollarSign, Mail,
  Check, Clock, Shield, Car, Briefcase, Award, Globe, Instagram, Facebook,
  ExternalLink, Building, ChevronLeft, ChevronRight, Heart, FileText, CreditCard, Send, Loader2,
} from "lucide-react";
import { GENRES, SHOOTING_STYLES, WORK_TYPES, GEAR_CATEGORIES, getLabel } from "@/lib/profileOptions";
import { StudioProfileView, VideoPortfolioDisplay } from "@/components/profile";

/* ── Prominent card wrapper ─────────────────────────────── */
const GlassCard = ({ children, className = "", testId }) => (
  <div
    className={`gs-card rounded-lg p-6 ${className}`}
    style={{ border: "1px solid rgba(255,255,255,0.18)" }}
    data-testid={testId}
  >
    {children}
  </div>
);

/* ── Star renderer ──────────────────────────────────────── */
function Stars({ rating }) {
  return (
    <span className="inline-flex gap-0.5">
      {[0, 1, 2, 3, 4].map((i) => (
        <Star
          key={i}
          className={`w-3.5 h-3.5 ${i < Math.round(rating) ? "text-yellow-400 fill-yellow-400" : "text-white/20"}`}
        />
      ))}
    </span>
  );
}

function Profile() {
  const { userId } = useParams();
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get("job_id");
  const viewParam = searchParams.get("view");
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = React.useState(null);
  const [reviews, setReviews] = React.useState([]);
  const [matchDetails, setMatchDetails] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [activeTab, setActiveTab] = React.useState("assistant");
  const [lightboxIdx, setLightboxIdx] = React.useState(null);
  const [isFavorited, setIsFavorited] = React.useState(false);

  const isOwnProfile = user && user.user_id === userId;

  React.useEffect(() => { fetchProfile(); fetchReviews(); }, [userId]);
  
  React.useEffect(() => {
    if (token && jobId && userId) {
      fetchMatchDetails();
    }
  }, [token, jobId, userId]);

  React.useEffect(() => {
    if (token && userId && !isOwnProfile) fetchFavoriteStatus();
  }, [token, userId]);

  React.useEffect(() => {
    if (profile) {
      const roles = profile.roles || [];
      if (viewParam === "studio" && roles.includes("studio")) {
        setActiveTab("studio");
      } else if (roles.includes("studio") && !roles.includes("assistant")) {
        setActiveTab("studio");
      }
    }
  }, [profile, viewParam]);

  function fetchProfile() {
    const headers = token ? { Authorization: "Bearer " + token } : {};
    fetch(API + "/users/profile/" + userId, { headers })
      .then((r) => { if (r.ok) return r.json(); throw new Error("Not found"); })
      .then((d) => { setProfile(d); setLoading(false); })
      .catch(() => { toast.error("Failed to load profile"); setLoading(false); });
  }
  
  function fetchMatchDetails() {
    fetch(`${API}/jobs/${jobId}/match-details/${userId}`, {
      headers: { Authorization: "Bearer " + token }
    })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => setMatchDetails(d))
      .catch(() => {});
  }

  function fetchReviews() {
    fetch(API + "/jobs/reviews/" + userId)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setReviews(Array.isArray(d) ? d : d.reviews || []))
      .catch(() => {});
  }

  function fetchFavoriteStatus() {
    fetch(API + "/favorites", { headers: { Authorization: "Bearer " + token } })
      .then((r) => (r.ok ? r.json() : { favorites: [] }))
      .then((d) => {
        const favs = d.favorites || d || [];
        setIsFavorited(Array.isArray(favs) && favs.some((f) => f.user_id === userId || f.favorite_id === userId));
      })
      .catch(() => {});
  }

  function toggleFavorite() {
    const method = isFavorited ? "DELETE" : "POST";
    fetch(API + "/favorites/" + userId, {
      method,
      headers: { Authorization: "Bearer " + token },
    })
      .then((r) => {
        if (r.ok) {
          setIsFavorited(!isFavorited);
          toast.success(isFavorited ? "Removed from favorites" : "Added to favorites");
        }
      })
      .catch(() => {});
  }

  function handleLogout() { logout().then(() => { window.location.href = "/"; }); }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-white/40 text-lg">Loading...</div>
      </div>
    );
  }
  if (!profile) return null;

  const ap = profile.assistant_profile || {};
  const sp = profile.studio_profile || {};
  const hasRole = (r) => (profile.roles || []).includes(r) || profile.role === r;
  const isAssistant = hasRole("assistant");
  const isStudio = hasRole("studio");
  const hasBothRoles = isAssistant && isStudio;

  const expertGenres = (ap.genres || []).filter((g) => g.level === "expert");
  const comfortableGenres = (ap.genres || []).filter((g) => g.level === "comfortable");
  const portfolio = ap.portfolio || [];

  const showAssistant = isAssistant && (activeTab === "assistant" || !hasBothRoles);
  const showStudio = isStudio && (activeTab === "studio" || (!hasBothRoles && !isAssistant));

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <NavBar>
        <Link to="/dashboard" className="text-sm font-medium text-white/60 hover:text-white transition-colors uppercase tracking-wider">Dashboard</Link>
        <NavActions />
      </NavBar>

      <main className="flex-1 pt-24 pb-16 px-4 md:px-10">
        <div className="max-w-[1400px] mx-auto">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-white/50 hover:text-white transition-colors mb-8" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm uppercase tracking-wider">Back</span>
          </button>

          {/* Role Tabs */}
          {hasBothRoles && (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
              <TabsList className="bg-white/[0.03] border border-white/10 p-1 rounded-lg">
                <TabsTrigger value="assistant" className="flex-1 data-[state=active]:bg-white data-[state=active]:text-black rounded-md gap-2">
                  <Camera className="w-4 h-4" /> For Hire
                </TabsTrigger>
                <TabsTrigger value="studio" className="flex-1 data-[state=active]:bg-white data-[state=active]:text-black rounded-md gap-2">
                  <Building className="w-4 h-4" /> Studio
                </TabsTrigger>
              </TabsList>
            </Tabs>
          )}

          {/* Studio tab */}
          {showStudio && <StudioProfileView profile={profile} studioProfile={sp} />}

          {/* Assistant two-column layout */}
          {showAssistant && (
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
              {/* ─── LEFT: Portfolio Gallery (3/5 width) ─────────── */}
              <div className="lg:col-span-3 space-y-6">
                {/* Portfolio hero */}
                {portfolio.length > 0 ? (
                  <GlassCard testId="portfolio-card" className="p-0 overflow-hidden">
                    <div className="p-5 pb-3 flex items-center justify-between">
                      <h2 className="text-lg font-bold tracking-tight">Portfolio</h2>
                      <span className="text-xs text-white/40">{portfolio.length} image{portfolio.length !== 1 ? "s" : ""}</span>
                    </div>
                    <MasonryGallery portfolio={portfolio} onImageClick={setLightboxIdx} />
                  </GlassCard>
                ) : (
                  <GlassCard testId="portfolio-card" className="text-center py-16">
                    <Camera className="w-10 h-10 text-white/15 mx-auto mb-3" />
                    <p className="text-white/40 text-sm">No portfolio images yet</p>
                  </GlassCard>
                )}

                {/* Video Portfolio */}
                {ap.portfolio_videos && ap.portfolio_videos.length > 0 && (
                  <GlassCard testId="video-portfolio-card">
                    <VideoPortfolioDisplay videos={ap.portfolio_videos} />
                  </GlassCard>
                )}

                {/* Bio */}
                {profile.bio && (
                  <GlassCard testId="bio-card">
                    <h2 className="text-lg font-bold mb-3">About</h2>
                    <p className="text-white/65 leading-relaxed whitespace-pre-wrap">{profile.bio}</p>
                  </GlassCard>
                )}

                {/* Genres & Styles */}
                {(expertGenres.length > 0 || comfortableGenres.length > 0 || (ap.shooting_styles && ap.shooting_styles.length > 0)) && (
                  <GlassCard testId="genres-card">
                    <h2 className="text-lg font-bold mb-4">Genres & Style</h2>
                    {expertGenres.length > 0 && (
                      <div className="mb-4">
                        <h3 className="text-white/50 text-xs uppercase tracking-wider mb-2">Expert In</h3>
                        <div className="flex flex-wrap gap-2">
                          {expertGenres.map((g) => (
                            <Badge key={g.genre} className="bg-coral-dim text-coral border-coral/30 rounded-md px-3 py-1 text-xs">{getLabel(GENRES, g.genre)}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {comfortableGenres.length > 0 && (
                      <div className="mb-4">
                        <h3 className="text-white/50 text-xs uppercase tracking-wider mb-2">Comfortable With</h3>
                        <div className="flex flex-wrap gap-2">
                          {comfortableGenres.map((g) => (
                            <Badge key={g.genre} className="bg-white/10 text-white/80 border-white/15 rounded-md">{getLabel(GENRES, g.genre)}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {ap.shooting_styles && ap.shooting_styles.length > 0 && (
                      <div>
                        <h3 className="text-white/50 text-xs uppercase tracking-wider mb-2">Shooting Styles</h3>
                        <div className="flex flex-wrap gap-2">
                          {ap.shooting_styles.map((s) => (
                            <Badge key={s} className="bg-white/10 text-white/80 border-white/15 rounded-md">{getLabel(SHOOTING_STYLES, s)}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </GlassCard>
                )}

                {/* Gear */}
                {ap.gear_items && ap.gear_items.length > 0 && (
                  <GlassCard testId="gear-card">
                    <h2 className="text-lg font-bold mb-4">Gear Bag</h2>
                    {GEAR_CATEGORIES.map((cat) => {
                      const items = ap.gear_items.filter((g) => g.category === cat.value);
                      if (!items.length) return null;
                      return (
                        <div key={cat.value} className="mb-4 last:mb-0">
                          <h3 className="text-white/50 text-xs uppercase tracking-wider mb-2">{cat.label}</h3>
                          <ul className="space-y-1">
                            {items.map((item, i) => (
                              <li key={i} className="flex items-center gap-2 text-white/70 text-sm">
                                <Camera className="w-3.5 h-3.5 text-white/30 shrink-0" />
                                {item.name}
                                {(item.quantity || 1) > 1 && <span className="text-[10px] text-white/40 bg-white/5 px-1.5 py-0.5 rounded">x{item.quantity}</span>}
                                {item.is_backup && <span className="text-[10px] text-white/40 bg-white/5 px-1.5 py-0.5 rounded">Backup</span>}
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                    {(ap.offers_editing || ap.has_backup_gear || ap.willing_to_rent) && (
                      <div className="mt-4 pt-4 border-t border-white/10 flex flex-wrap gap-4">
                        {ap.willing_to_rent && (
                          <span className="flex items-center gap-1.5 text-coral text-xs"><Check className="w-3.5 h-3.5" />Willing to Rent Gear</span>
                        )}
                        {ap.has_backup_gear && (
                          <span className="flex items-center gap-1.5 text-coral text-xs"><Check className="w-3.5 h-3.5" />Backup Equipment</span>
                        )}
                        {ap.offers_editing && (
                          <span className="flex items-center gap-1.5 text-coral text-xs"><Check className="w-3.5 h-3.5" />Editing Services</span>
                        )}
                      </div>
                    )}
                  </GlassCard>
                )}

                {/* Reviews */}
                <GlassCard testId="reviews-card">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold">Reviews ({reviews.length})</h2>
                    {isOwnProfile && (
                      <RequestReviewDialog token={token} />
                    )}
                  </div>
                  {reviews.length === 0 ? (
                    <p className="text-white/40 text-sm">No reviews yet</p>
                  ) : (
                    <div className="space-y-5">
                      {reviews.map((r) => (
                        <div key={r.review_id} className="border-b border-white/8 pb-5 last:border-0 last:pb-0">
                          <div className="flex items-start justify-between mb-1.5">
                            <div>
                              <p className="font-semibold text-sm">{r.reviewer_name || r.reviewer?.name || "Anonymous"}</p>
                              <Stars rating={r.rating} />
                            </div>
                            <div className="text-right">
                              <span className="text-white/30 text-xs">{new Date(r.created_at).toLocaleDateString()}</span>
                              {r.source === "external_request" && (
                                <Badge className="block mt-1 bg-white/[0.06] text-white/40 border-white/10 text-[10px]">External</Badge>
                              )}
                            </div>
                          </div>
                          <p className="text-white/60 text-sm mt-2">{r.comment}</p>
                          {r.would_hire_again != null && (
                            <Badge className={r.would_hire_again ? "mt-2 bg-green-500/15 text-green-400 border-green-500/20 text-[10px]" : "mt-2 bg-red-500/15 text-red-400 border-red-500/20 text-[10px]"}>
                              {r.would_hire_again ? "Would hire again" : "Would not hire again"}
                            </Badge>
                          )}
                          {r.roles_performed && r.roles_performed.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {r.roles_performed.map((role) => (
                                <Badge key={role} className="bg-white/[0.04] text-white/40 border-white/8 text-[10px]">
                                  {getLabel(WORK_TYPES, role)}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </GlassCard>
              </div>

              {/* ─── RIGHT: Data Cards (2/5 width) ──────────────── */}
              <div className="lg:col-span-2 space-y-6">
                {/* Identity card */}
                <GlassCard testId="profile-card">
                  <div className="flex items-start gap-4 mb-4">
                    <Avatar className="w-20 h-20 border-2 border-white/15 shrink-0">
                      <AvatarImage src={profile.profile_image} />
                      <AvatarFallback className="bg-white/[0.05] text-white text-2xl font-bold">
                        {profile.name ? profile.name.charAt(0).toUpperCase() : "?"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <h1 className="text-xl font-bold truncate">{profile.name}</h1>
                          {profile.is_pro && (
                            <span className="bg-gradient-to-r from-amber-500 to-yellow-400 text-black text-[9px] font-black px-2 py-0.5 rounded-full shrink-0" data-testid="pro-badge">
                              PRO
                            </span>
                          )}
                        </div>
                        {!isOwnProfile && token && (
                          <button onClick={toggleFavorite} className="shrink-0 ml-2 p-1.5 rounded-md hover:bg-white/10 transition-colors" data-testid="favorite-btn">
                            <Heart className={`w-5 h-5 transition-colors ${isFavorited ? "text-coral fill-coral" : "text-white/30 hover:text-coral"}`} />
                          </button>
                        )}
                      </div>
                      <p className="text-white/45 text-sm">
                        {(profile.roles || []).map((r) => (r === "studio" ? "Studio" : "Second Shooter")).join(" & ") || (profile.role === "studio" ? "Studio" : "Second Shooter")}
                      </p>
                      {profile.rating && (
                        <div className="flex items-center gap-1.5 mt-1">
                          <Stars rating={profile.rating} />
                          <span className="text-white/50 text-xs">{profile.rating.toFixed(1)}</span>
                          {profile.would_hire_again_pct != null && (
                            <span className="text-green-400/70 text-xs ml-2">{profile.would_hire_again_pct}% would hire again</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Location */}
                  {(() => {
                    const loc = profile.location || profile.assistant_profile?.location_info;
                    return loc?.city ? (
                      <div className="flex items-center gap-2 text-white/55 text-sm mb-4">
                        <MapPin className="w-4 h-4 shrink-0" />
                        <span>{loc.city}, {loc.state}</span>
                      </div>
                    ) : null;
                  })()}

                  {/* Action buttons */}
                  <div className="space-y-2">
                    {isOwnProfile ? (
                      <>
                        {isAssistant && (
                          <Link to="/profile/edit" className="block">
                            <Button className="w-full bg-white text-black hover:bg-gray-200 rounded-md font-bold text-sm" data-testid="edit-profile-btn">
                              <Camera className="w-4 h-4 mr-2" />Edit For Hire Profile
                            </Button>
                          </Link>
                        )}
                        {isStudio && (
                          <Link to="/profile/edit/studio" className="block">
                            <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/10 rounded-md text-sm" data-testid="edit-studio-btn">
                              <Building className="w-4 h-4 mr-2" />Edit Studio Profile
                            </Button>
                          </Link>
                        )}
                      </>
                    ) : token ? (
                      <div className="space-y-2">
                        <Button
                          className="w-full bg-white text-black hover:bg-gray-200 rounded-md font-bold text-sm"
                          data-testid="message-btn"
                          onClick={() => {
                            const params = new URLSearchParams({ recipient_id: userId, content: "Hi, I'd like to connect!" });
                            fetch(API + "/messages/send?" + params.toString(), {
                              method: "POST",
                              headers: { Authorization: "Bearer " + token },
                            })
                              .then(r => r.ok ? r.json() : Promise.reject())
                              .then(d => navigate("/messages/" + d.thread_id))
                              .catch(() => toast.error("Failed to start conversation"));
                          }}
                        >
                          <Mail className="w-4 h-4 mr-2" />Message
                        </Button>
                        {jobId && (
                          <Link to={"/jobs/" + jobId + "/candidates"}>
                            <Button variant="outline" className="w-full border-coral/40 text-coral hover:bg-coral/10 rounded-md text-sm" data-testid="hire-from-profile-btn">
                              <Briefcase className="w-4 h-4 mr-2" />Hire for Job
                            </Button>
                          </Link>
                        )}
                      </div>
                    ) : (
                      <Link to="/login">
                        <Button className="w-full bg-white text-black hover:bg-gray-200 rounded-md font-bold text-sm">
                          <Mail className="w-4 h-4 mr-2" />Sign In to Contact
                        </Button>
                      </Link>
                    )}
                  </div>

                  {/* Verification badges */}
                  <div className="mt-5 pt-4 border-t border-white/10 space-y-2">
                    {ap.has_insurance && (
                      <div className="flex items-center gap-2 text-coral text-sm">
                        <Shield className="w-4 h-4" />Insured{ap.insurance_verified && <Check className="w-3 h-3" />}
                      </div>
                    )}
                    {ap.has_transportation && (
                      <div className="flex items-center gap-2 text-white/55 text-sm"><Car className="w-4 h-4" />Own Transportation</div>
                    )}
                    {ap.response_time_hours && (
                      <div className="flex items-center gap-2 text-white/55 text-sm"><Clock className="w-4 h-4" />Replies in ~{ap.response_time_hours}h</div>
                    )}
                    {ap.availability_settings && (
                      <Badge className={(profile.availability !== false ? "bg-coral-dim text-coral border-coral/30" : "bg-red-500/20 text-red-400 border-red-500/30") + " rounded-md px-2.5 py-1 text-xs mt-2 inline-flex"}>
                        {profile.availability !== false ? "Available" : "Not Available"}
                      </Badge>
                    )}
                  </div>
                </GlassCard>

                {/* Quick stats */}
                <GlassCard testId="quick-facts-card">
                  <h2 className="text-sm font-bold uppercase tracking-wider text-white/50 mb-4">Quick Facts</h2>
                  <div className="grid grid-cols-2 gap-3">
                    <StatBox icon={<Award className="w-5 h-5" />} value={ap.years_experience || 0} label="Yrs Exp" />
                    <StatBox
                      icon={<Camera className="w-5 h-5" />}
                      value={Object.values(ap.completed_shoots || {}).reduce((a, b) => a + b, 0)}
                      label="Shoots"
                    />
                    <StatBox
                      icon={<DollarSign className="w-5 h-5" />}
                      value={ap.rate_min && ap.rate_max ? `$${ap.rate_min}–${ap.rate_max}` : ap.rate_min ? `$${ap.rate_min}+` : "—"}
                      label="Per Hour"
                    />
                    <StatBox icon={<Star className="w-5 h-5" />} value={profile.review_count || 0} label="Reviews" />
                  </div>
                </GlassCard>

                {/* Match Details - Show when viewing from a job */}
                {matchDetails && (
                  <GlassCard testId="match-details-card" className="border-coral/30 bg-coral/5">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-sm font-bold uppercase tracking-wider text-coral">Match Analysis</h2>
                      <Badge className={`${matchDetails.match_tier === "excellent" ? "bg-coral/20 text-coral" : matchDetails.match_tier === "good" ? "bg-white/20 text-white" : "bg-white/10 text-white/60"} rounded-full px-3 py-1 text-xs font-bold`}>
                        {matchDetails.match_score}% Match
                      </Badge>
                    </div>
                    <div className="space-y-2 text-sm">
                      {matchDetails.factors && Object.entries(matchDetails.factors).map(([key, value]) => (
                        <div key={key} className="flex justify-between items-center">
                          <span className="text-white/50 capitalize">{key.replace(/_/g, " ")}</span>
                          <span className={`font-medium ${(() => {
                            const v = value.toLowerCase();
                            const isNeg = v.startsWith("no ") || v.includes("not specified") || v.includes("n/a");
                            const isPos = !isNeg && (v.includes("match") || v.includes("expert") || v.includes("compatible") || v.includes("experience") || v.includes("skills") || v.includes("comfortable") || v.includes("willing"));
                            return isPos ? "text-coral" : "text-white/70";
                          })()}`}>
                            {value}
                          </span>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-white/40 mt-3 pt-3 border-t border-white/10">
                      Based on {matchDetails.job_title}
                    </p>
                  </GlassCard>
                )}

                {/* Work types */}
                {ap.work_types && ap.work_types.length > 0 && (
                  <GlassCard testId="work-types-card">
                    <h2 className="text-sm font-bold uppercase tracking-wider text-white/50 mb-3">Available As</h2>
                    <div className="flex flex-wrap gap-2">
                      {ap.work_types.map((t, i) => {
                        const typeName = typeof t === "object" ? t.type : t;
                        const rateMin = typeof t === "object" ? t.rate_min : null;
                        const rateMax = typeof t === "object" ? t.rate_max : null;
                        const rateStr = rateMin && rateMax ? ` ($${rateMin}-$${rateMax}/hr)` : rateMin ? ` (from $${rateMin}/hr)` : "";
                        return (
                          <Badge key={i} className="bg-white/8 text-white/80 border-white/15 rounded-md px-3 py-1 text-xs">
                            {getLabel(WORK_TYPES, typeName)}{rateStr}
                          </Badge>
                        );
                      })}
                    </div>
                  </GlassCard>
                )}

                {/* Experience details */}
                <GlassCard testId="experience-card">
                  <h2 className="text-sm font-bold uppercase tracking-wider text-white/50 mb-4">Experience</h2>
                  <dl className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-white/45">Level</dt>
                      <dd className="font-medium capitalize">{(ap.experience_level || "novice").replace("_", " ")}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-white/45">Years</dt>
                      <dd className="font-medium">{ap.years_experience || 0}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-white/45">Type</dt>
                      <dd className="font-medium">{ap.employment_type === "full_time" ? "Full-Time" : "Part-Time"}</dd>
                    </div>
                    {ap.has_been_lead && (
                      <div className="flex items-center gap-1.5 text-coral text-xs pt-1">
                        <Check className="w-3.5 h-3.5" />Has led shoots
                      </div>
                    )}
                  </dl>
                </GlassCard>

                {/* Social & External Links — prominent */}
                {(ap.portfolio_link || ap.instagram_handle || ap.facebook_url || ap.tiktok_handle || profile.instagram) && (
                  <GlassCard testId="links-card">
                    <h2 className="text-sm font-bold uppercase tracking-wider text-white/50 mb-4">Links</h2>
                    <div className="space-y-2.5">
                      {(ap.instagram_handle || profile.instagram) && (
                        <a href={`https://instagram.com/${(ap.instagram_handle || profile.instagram || "").replace("@", "")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-white/70 hover:text-white transition-colors group" data-testid="instagram-link">
                          <div className="w-8 h-8 rounded-md bg-white/5 flex items-center justify-center group-hover:bg-white/10 transition-colors shrink-0">
                            <Instagram className="w-4 h-4" />
                          </div>
                          <span className="text-sm">@{(ap.instagram_handle || profile.instagram || "").replace("@", "")}</span>
                          <ExternalLink className="w-3 h-3 ml-auto shrink-0 text-white/30" />
                        </a>
                      )}
                      {ap.tiktok_handle && (
                        <a href={`https://tiktok.com/@${ap.tiktok_handle.replace("@", "")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-white/70 hover:text-white transition-colors group" data-testid="tiktok-link">
                          <div className="w-8 h-8 rounded-md bg-white/5 flex items-center justify-center group-hover:bg-white/10 transition-colors shrink-0">
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                            </svg>
                          </div>
                          <span className="text-sm">@{ap.tiktok_handle.replace("@", "")}</span>
                          <ExternalLink className="w-3 h-3 ml-auto shrink-0 text-white/30" />
                        </a>
                      )}
                      {ap.facebook_url && (
                        <a href={ap.facebook_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-white/70 hover:text-white transition-colors group" data-testid="facebook-link">
                          <div className="w-8 h-8 rounded-md bg-white/5 flex items-center justify-center group-hover:bg-white/10 transition-colors shrink-0">
                            <Facebook className="w-4 h-4" />
                          </div>
                          <span className="text-sm truncate">{ap.facebook_url.replace(/^https?:\/\/(www\.)?facebook\.com\//, "")}</span>
                          <ExternalLink className="w-3 h-3 ml-auto shrink-0 text-white/30" />
                        </a>
                      )}
                      {ap.portfolio_link && (
                        <a href={ap.portfolio_link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-white/70 hover:text-white transition-colors group" data-testid="portfolio-link">
                          <div className="w-8 h-8 rounded-md bg-white/5 flex items-center justify-center group-hover:bg-white/10 transition-colors shrink-0">
                            <Camera className="w-4 h-4" />
                          </div>
                          <span className="text-sm truncate">Portfolio Site</span>
                          <ExternalLink className="w-3 h-3 ml-auto shrink-0 text-white/30" />
                        </a>
                      )}
                    </div>
                  </GlassCard>
                )}

                {/* Instagram Feed Preview */}
                {/* Removed: Instagram API requires app review. Link is in LINKS section above. */}

                {/* Payment Info on File indicator */}
                {(ap.w9_uploaded || (ap.payment_methods && ap.payment_methods.length > 0)) && (
                  <GlassCard testId="payment-info-card">
                    <h2 className="text-sm font-bold uppercase tracking-wider text-white/50 mb-4">Payment Info</h2>
                    <div className="space-y-3">
                      {ap.w9_uploaded && (
                        <div className="flex items-center gap-3 text-white/70">
                          <div className="w-8 h-8 rounded-md bg-green-500/10 flex items-center justify-center shrink-0">
                            <FileText className="w-4 h-4 text-green-400" />
                          </div>
                          <div>
                            <span className="text-sm font-medium text-green-400">W9 on file</span>
                            <p className="text-xs text-white/40">Available after hiring</p>
                          </div>
                        </div>
                      )}
                      {ap.payment_methods && ap.payment_methods.length > 0 && (
                        <div className="flex items-center gap-3 text-white/70">
                          <div className="w-8 h-8 rounded-md bg-blue-500/10 flex items-center justify-center shrink-0">
                            <CreditCard className="w-4 h-4 text-blue-400" />
                          </div>
                          <div>
                            <span className="text-sm font-medium text-blue-400">
                              {ap.payment_methods.length} payment method{ap.payment_methods.length > 1 ? 's' : ''} on file
                            </span>
                            <p className="text-xs text-white/40">Details available after hiring</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </GlassCard>
                )}

                {/* Notable Clients */}
                {ap.notable_clients && ap.notable_clients.length > 0 && (
                  <GlassCard testId="clients-card">
                    <h2 className="text-sm font-bold uppercase tracking-wider text-white/50 mb-3">Notable Clients</h2>
                    <ul className="space-y-2">
                      {ap.notable_clients.map((client, i) => (
                        <li key={i} className="flex items-center gap-2 text-white/70 text-sm">
                          <Briefcase className="w-3.5 h-3.5 text-white/30 shrink-0" />{client}
                        </li>
                      ))}
                    </ul>
                  </GlassCard>
                )}

                {/* Languages & Certifications */}
                {((ap.languages && ap.languages.length > 0) || (ap.certifications && ap.certifications.length > 0)) && (
                  <GlassCard testId="languages-card">
                    {ap.languages && ap.languages.length > 0 && (
                      <div className="mb-4">
                        <h3 className="text-white/50 text-xs uppercase tracking-wider mb-2">Languages</h3>
                        <div className="flex flex-wrap gap-2">
                          {ap.languages.map((lang) => (
                            <Badge key={lang} className="bg-white/8 text-white/80 border-white/15 rounded-md text-xs">{lang}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {ap.certifications && ap.certifications.length > 0 && (
                      <div>
                        <h3 className="text-white/50 text-xs uppercase tracking-wider mb-2">Certifications</h3>
                        <ul className="space-y-1.5">
                          {ap.certifications.map((cert) => (
                            <li key={cert} className="flex items-center gap-2 text-white/70 text-xs">
                              <Award className="w-3.5 h-3.5 text-white/30 shrink-0" />
                              {cert.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </GlassCard>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />

      {/* Lightbox */}
      {lightboxIdx !== null && portfolio.length > 0 && (
        <Lightbox
          images={portfolio}
          idx={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
          onChange={setLightboxIdx}
        />
      )}
    </div>
  );
}

/* ── Masonry Gallery ─────────────────────────────────────── */
function MasonryGallery({ portfolio, onImageClick }) {
  const [loaded, setLoaded] = React.useState({});
  const cols = 3;

  // Distribute items across columns for true masonry
  const columns = Array.from({ length: cols }, () => []);
  portfolio.forEach((item, idx) => {
    columns[idx % cols].push({ ...item, idx });
  });

  return (
    <div className="flex gap-1 px-1 pb-1" data-testid="masonry-gallery">
      {columns.map((col, colIdx) => (
        <div key={colIdx} className="flex-1 flex flex-col gap-1">
          {col.map(({ url, tags, idx }) => (
            <button
              key={idx}
              onClick={() => onImageClick(idx)}
              className="w-full overflow-hidden relative group bg-white/[0.03] focus:outline-none rounded-sm"
              data-testid={`portfolio-img-${idx}`}
            >
              <img
                src={url}
                alt={`Portfolio ${idx + 1}`}
                className={`w-full h-auto block transition-all duration-500 group-hover:scale-[1.03] ${loaded[idx] ? "opacity-100" : "opacity-0"}`}
                loading="lazy"
                onLoad={() => setLoaded((p) => ({ ...p, [idx]: true }))}
              />
              {!loaded[idx] && (
                <div className="aspect-[4/3] bg-white/[0.03] animate-pulse" />
              )}
              {tags && tags.length > 0 && (
                <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="flex flex-wrap gap-1">
                    {tags.slice(0, 2).map((tag) => (
                      <span key={tag} className="text-[10px] bg-white/20 text-white px-1.5 py-0.5 rounded">{getLabel(GENRES, tag)}</span>
                    ))}
                  </div>
                </div>
              )}
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

/* ── Stat box in Quick Facts ────────────────────────────── */
function StatBox({ icon, value, label }) {
  return (
    <div className="text-center p-3 rounded-md bg-white/[0.04] border border-white/8">
      <div className="text-white/40 mb-1 flex justify-center">{icon}</div>
      <div className="text-lg font-bold leading-tight">{value}</div>
      <div className="text-white/40 text-[10px] uppercase tracking-wider mt-0.5">{label}</div>
    </div>
  );
}

/* ── Lightbox overlay ───────────────────────────────────── */
function Lightbox({ images, idx, onClose, onChange }) {
  React.useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && idx > 0) onChange(idx - 1);
      if (e.key === "ArrowRight" && idx < images.length - 1) onChange(idx + 1);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [idx, images.length, onClose, onChange]);

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={onClose} data-testid="lightbox-overlay">
      <button onClick={onClose} className="absolute top-4 right-4 text-white/60 hover:text-white text-2xl z-10">&times;</button>
      {idx > 0 && (
        <button onClick={(e) => { e.stopPropagation(); onChange(idx - 1); }} className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white/70 hover:bg-white/20">
          <ChevronLeft className="w-5 h-5" />
        </button>
      )}
      <img
        src={images[idx].url}
        alt={`Portfolio ${idx + 1}`}
        className="max-h-[85vh] max-w-[90vw] object-contain rounded-md"
        onClick={(e) => e.stopPropagation()}
      />
      {idx < images.length - 1 && (
        <button onClick={(e) => { e.stopPropagation(); onChange(idx + 1); }} className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white/70 hover:bg-white/20">
          <ChevronRight className="w-5 h-5" />
        </button>
      )}
      <div className="absolute bottom-4 text-white/40 text-sm">{idx + 1} / {images.length}</div>
    </div>
  );
}

/* ── Request Review Dialog ───────────────────────────────── */
function RequestReviewDialog({ token }) {
  const [open, setOpen] = React.useState(false);
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [roles, setRoles] = React.useState([]);
  const [message, setMessage] = React.useState("");
  const [sending, setSending] = React.useState(false);

  function toggleRole(val) {
    setRoles(function(prev) {
      return prev.includes(val) ? prev.filter(function(r) { return r !== val; }) : [...prev, val];
    });
  }

  // Sending it yourself lands far more often than a message from a platform
  // the recipient has never heard of, so it is the default.
  // React.useState, as everywhere else in this file — the bare form threw
  // "useState is not defined" and took the whole page down, because only the
  // React default export is imported here.
  const [delivery, setDelivery] = React.useState("self");
  const [draft, setDraft] = React.useState(null);

  const resetForm = function () {
    setName("");
    setEmail("");
    setRoles([]);
    setMessage("");
    setDraft(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Please enter their name"); return; }
    if (!email.trim()) { toast.error("Please enter their email"); return; }
    if (roles.length === 0) { toast.error("Please select at least one role"); return; }

    setSending(true);
    try {
      const res = await fetch(API + "/reviews/request", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          recipient_email: email.trim(),
          recipient_name: name.trim(),
          roles_performed: roles,
          message: message.trim() || null,
          delivery: delivery
        })
      });

      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || "Failed to create request");
        return;
      }

      if (data.delivery === "self") {
        setDraft(data);
      } else if (data.status === "send_failed") {
        // Do not claim success when the send failed — offer the fallback.
        toast.error("We couldn't send that email. Copy the message and send it yourself instead.");
        setDraft(data);
      } else {
        toast.success("Review request sent!");
        setOpen(false);
        resetForm();
      }
    } catch (e) {
      toast.error("Failed to create request");
    } finally {
      setSending(false);
    }
  };

  const copyDraft = async function () {
    try {
      await navigator.clipboard.writeText(draft.draft_body || draft.review_url);
      toast.success("Copied — paste it into your email");
    } catch {
      toast.error("Could not copy. Select the text and copy it manually.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="border-white/20 text-white/70 hover:bg-white/10" data-testid="request-review-btn">
          <Send className="w-3.5 h-3.5 mr-1.5" /> Request Review
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#0a0a0a] border-white/10 text-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Request a Review</DialogTitle>
        </DialogHeader>
        {draft ? (
          <div className="space-y-4 mt-4" data-testid="profile-rr-draft">
            <p className="text-white/60 text-sm">
              Ready to send. Paste this into an email to <span className="text-white">{email}</span> —
              coming from your own address, it is far more likely to be read.
            </p>
            <Textarea
              readOnly
              value={draft.draft_body || draft.review_url}
              className="bg-white/[0.05] border-white/10 text-white min-h-[190px] text-sm"
              onFocus={function (e) { e.target.select(); }}
              data-testid="profile-rr-draft-text"
            />
            <div className="flex gap-2">
              <Button
                type="button"
                onClick={copyDraft}
                className="flex-1 bg-coral hover:bg-coral/90 text-white font-bold"
                data-testid="profile-rr-copy"
              >
                Copy message
              </Button>
              <a
                href={
                  "mailto:" + encodeURIComponent(email) +
                  "?subject=" + encodeURIComponent(draft.draft_subject || "Would you leave me a review?") +
                  "&body=" + encodeURIComponent(draft.draft_body || draft.review_url)
                }
                className="flex-1"
              >
                <Button type="button" variant="outline" className="w-full border-white/20 text-white hover:bg-white/10">
                  Open in email
                </Button>
              </a>
            </div>
            <p className="text-xs text-white/30">
              The link works without an account and stays valid until they leave the review.
            </p>
            <Button
              type="button"
              variant="ghost"
              onClick={function () { setOpen(false); resetForm(); }}
              className="w-full text-white/50 hover:text-white"
            >
              Done
            </Button>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <p className="text-white/50 text-sm">
            Enter the name and email of someone you've worked with — on any job, not just ones booked here.
          </p>
          <div>
            <label className="text-sm text-white/60 mb-1.5 block">Their Name *</label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Sarah Johnson"
              className="bg-white/[0.05] border-white/10 text-white"
              required
              data-testid="profile-rr-name"
            />
          </div>
          <div>
            <label className="text-sm text-white/60 mb-1.5 block">Their Email *</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Their email address"
              className="bg-white/[0.05] border-white/10 text-white"
              required
              data-testid="profile-rr-email"
            />
          </div>
          <div>
            <label className="text-sm text-white/60 mb-1.5 block">Roles You Performed *</label>
            <div className="grid grid-cols-2 gap-2 max-h-36 overflow-y-auto pr-1">
              {WORK_TYPES.map(function(wt) {
                var selected = roles.includes(wt.value);
                return (
                  <button
                    key={wt.value}
                    type="button"
                    onClick={function() { toggleRole(wt.value); }}
                    className={"text-left text-xs px-3 py-2 rounded-sm border transition-all " + (selected ? "border-coral bg-coral/15 text-coral" : "border-white/10 bg-white/[0.03] text-white/60 hover:border-white/20")}
                  >
                    {wt.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="text-sm text-white/60 mb-1.5 block">Personal Message (optional)</label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add a personal note..."
              className="bg-white/[0.05] border-white/10 text-white min-h-[70px]"
            />
          </div>
          <div>
            <label className="text-sm text-white/60 mb-1.5 block">How should it reach them?</label>
            <div className="space-y-2">
              <button
                type="button"
                onClick={function () { setDelivery("self"); }}
                className={"w-full text-left px-3 py-2.5 rounded-sm border transition-all " + (delivery === "self" ? "border-coral bg-coral/15" : "border-white/10 bg-white/[0.03] hover:border-white/20")}
                data-testid="profile-rr-delivery-self"
              >
                <span className={"text-sm font-medium " + (delivery === "self" ? "text-coral" : "text-white/80")}>
                  I'll send it myself
                </span>
                <span className="block text-xs text-white/40 mt-0.5">
                  We write it, you paste it into your own email. Recommended — it comes from someone they know.
                </span>
              </button>
              <button
                type="button"
                onClick={function () { setDelivery("gottasecond"); }}
                className={"w-full text-left px-3 py-2.5 rounded-sm border transition-all " + (delivery === "gottasecond" ? "border-coral bg-coral/15" : "border-white/10 bg-white/[0.03] hover:border-white/20")}
                data-testid="profile-rr-delivery-platform"
              >
                <span className={"text-sm font-medium " + (delivery === "gottasecond" ? "text-coral" : "text-white/80")}>
                  Send it for me
                </span>
                <span className="block text-xs text-white/40 mt-0.5">
                  GottaSecond emails them. May land in spam if they don't know us.
                </span>
              </button>
            </div>
          </div>
          <Button
            type="submit"
            disabled={sending}
            className="w-full bg-coral hover:bg-coral/90 text-white font-bold"
            data-testid="profile-rr-submit"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : delivery === "self" ? "Get the message" : "Send Request"}
          </Button>
        </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default Profile;
